
import { onAuthChange } from "./jsScripts/firebase.js";
import { loadCourses, saveSemester, loadSemesters, saveCourse, saveEvaluation } from "./jsScripts/db.js";
import { createNewTable, attachEventListeners, attachAllEventListeners } from "./jsScripts/tableOps.js";
import { calculateCurrentGPA } from "./jsScripts/gradeCalc.js";
import { markSaving, markSaved, markSaveFailed } from "./jsScripts/utils.js";

let currentSemesterId = new URLSearchParams(window.location.search).get("semesterId");
const semesterNameInput = document.getElementById("semesterName");

// Create the active semester at most once, shared between the semester-name
// auto-save and the first-load bootstrap, so a fast typist cannot create a
// duplicate default semester.
let semesterCreating = null;
function ensureSemester(name) {
  if (currentSemesterId) return Promise.resolve(currentSemesterId);
  if (!semesterCreating) {
    semesterCreating = Promise.resolve(saveSemester({ name: name || "My Semester", startDate: null, endDate: null }))
      .then((id) => {
        if (id) {
          currentSemesterId = id;
          const url = new URL(window.location);
          url.searchParams.set("semesterId", id);
          window.history.replaceState({}, "", url);
          try { localStorage.setItem('lastSemesterId', id); } catch (_) {}
        }
        return id;
      });
  }
  return semesterCreating;
}

// Function to save semester name when user types
let saveSemesterTimeout;
let savingSemester = false;
let pendingSemesterSave = false;
async function saveSemesterName() {
  const name = semesterNameInput?.value.trim();
  if (!name) return;

  // If a save is already in flight, mark that another is needed and run it once
  // the current one finishes, so the latest typed value is never dropped.
  if (savingSemester) { pendingSemesterSave = true; return; }
  savingSemester = true;
  markSaving();

  try {
    // If we don't yet have an id, reuse an existing semester with the same name.
    if (!currentSemesterId) {
      const existing = (await loadSemesters()).find(s => (s.name || "").trim() === name);
      if (existing) currentSemesterId = existing.id;
    }
    // Otherwise create the one shared semester (deduped with the first-load
    // bootstrap so two are never created).
    if (!currentSemesterId) {
      await ensureSemester(name);
    }

    // Persist the typed name onto the active semester.
    const semesterId = await saveSemester({
      name,
      startDate: null,
      endDate: null,
      semesterId: currentSemesterId
    });
    
    if (semesterId) {
      currentSemesterId = semesterId;
      // Always ensure URL matches the active semester id
      const url = new URL(window.location);
      if (url.searchParams.get("semesterId") !== semesterId) {
        url.searchParams.set("semesterId", semesterId);
        window.history.replaceState({}, "", url);
      }
      console.log("✅ Semester saved:", name, semesterId);
      // Now that we have a semester id, ensure all existing tables are wired to save
      attachAllEventListeners();
      // Remember last used semester for cross-page navigation
      try { localStorage.setItem('lastSemesterId', currentSemesterId); } catch(_) {}
      markSaved();
    } else {
      markSaveFailed();
    }
  } catch (err) {
    console.error("❌ Failed to save semester:", err);
    markSaveFailed();
  } finally {
    savingSemester = false;
    if (pendingSemesterSave) {
      pendingSemesterSave = false;
      saveSemesterName();
    }
  }
}

// Auto-save semester name when user types (with debounce)
if (semesterNameInput) {
  semesterNameInput.addEventListener("input", () => {
    clearTimeout(saveSemesterTimeout);
    saveSemesterTimeout = setTimeout(saveSemesterName, 1000); // Wait 1 second after typing stops
  });
  
  semesterNameInput.addEventListener("blur", () => {
    clearTimeout(saveSemesterTimeout);
    saveSemesterName(); // Save immediately when they leave the field
  });
}

// Clear every rendered course table. The auth listener below runs again on
// every sign-in and sign-out (including one triggered in another tab), and it
// used to append its tables to whatever was already on screen, leaving a second
// full copy of every course stacked under the first.
function resetTables() {
  document.querySelectorAll(".table-wrapper").forEach((wrapper) => wrapper.remove());
}

// Only the newest render may touch the DOM, so two auth emissions arriving
// close together cannot interleave their results.
let renderToken = 0;

// Load courses and semester name when auth state is determined
onAuthChange(async (user) => {
  const token = ++renderToken;
  try {
    // Load semesters and ensure we have a valid semesterId (persisted across pages)
    const semesters = await loadSemesters();
    if (!currentSemesterId) {
      // Try to reuse last visited semester
      let last = null;
      try { last = localStorage.getItem('lastSemesterId'); } catch(_) {}
      if (last && semesters.find(s => s.id === last)) {
        currentSemesterId = last;
      } else if (semesters.length > 0) {
        // Fallback to the most recent semester
        currentSemesterId = semesters[semesters.length - 1].id;
      }
      if (currentSemesterId) {
        const url = new URL(window.location);
        url.searchParams.set('semesterId', currentSemesterId);
        window.history.replaceState({}, '', url);
        try { localStorage.setItem('lastSemesterId', currentSemesterId); } catch(_) {}
      }
    }

    // Load semester name if we have a semesterId; if not found, recover gracefully
    if (currentSemesterId) {
      let semester = semesters.find(s => s.id === currentSemesterId);
      if (!semester && semesterNameInput?.value) {
        // Try to match by name if id mismatch
        semester = semesters.find(s => (s.name || "").trim() === semesterNameInput.value.trim());
        if (semester) currentSemesterId = semester.id;
      }
      if (!semester && semesters.length > 0) {
        // Fallback to the most recent
        semester = semesters[semesters.length - 1];
        currentSemesterId = semester.id;
      }
      if (semester && semesterNameInput) {
        semesterNameInput.value = semester.name || "";
        // Ensure URL reflects recovered id
        const url = new URL(window.location);
        if (url.searchParams.get("semesterId") !== currentSemesterId) {
          url.searchParams.set("semesterId", currentSemesterId);
          window.history.replaceState({}, "", url);
        }
        try { localStorage.setItem('lastSemesterId', currentSemesterId); } catch(_) {}
      }
    }
    
    // Load courses
    if (!currentSemesterId) {
      // First-time visitor with no semester yet: create a default one (shared
      // with the semester-name auto-save so two are never created) so the save
      // wiring attaches and nothing the user types is lost.
      await ensureSemester("My Semester");
      if (token !== renderToken) return;
      resetTables();
      createNewTable([]);
      return;
    }

    const courses = await loadCourses(currentSemesterId);
    console.log("📚 Loaded courses:", courses);
    if (token !== renderToken) return;

    // Every table on screen belongs to the identity we just replaced, so clear
    // them all and render this account's courses from scratch.
    resetTables();

    if (courses.length === 0) {
      // No saved courses → show a single blank table
      createNewTable([]);
    } else {
      courses.forEach((course) => {
        const { id, code, topic, units, evaluations = [] } = course;

        const table = createNewTable(evaluations);

        table.dataset.courseId = id;

        const codeInput = table.querySelector(".courseCode");
        const topicInput = table.querySelector(".courseTopic");
        const unitsDropdown = table.querySelector(".courseUnitsDropdown");

        if (codeInput) codeInput.value = code || "";
        if (topicInput) topicInput.value = topic || "";
        if (unitsDropdown) unitsDropdown.value = (units != null ? Number(units).toFixed(2) : "0.50");

        attachEventListeners(table);
      });

      // Recalculate GPA after all courses are loaded with their correct units
      calculateCurrentGPA();

      // Safety: if for any reason no table wrapper is present, ensure one exists
      if (!document.querySelector('.table-wrapper')) {
        createNewTable([]);
      }
    }
  } catch (err) {
    console.error("❌ Failed to load or render courses:", err);
    if (token !== renderToken) return;
    // Show blank table on error
    resetTables();
    createNewTable([]);
  }
});

    